/**
 * The File Uploads drawer's logic without React (IP-103, IP-108; design
 * 2026-10-02 sections 3, 6 "Intake" and 8 "The drawer"):
 *
 *   the class summary    a batch's summary (written by its class's parser at
 *                        the scan) as facts to read: its plain values, the
 *                        findings, counts and warnings left to their own
 *                        blocks (summaryFacts)
 *   the warnings block   a VALIDATED batch's summary.warnings (findings.js
 *                        warningsSummary): { total, byRule, messages,
 *                        items - the first 50 as {rowNo, rule, column,
 *                        value} - truncated }; the full list is paged with
 *                        "Rows with findings only" (warningsOf)
 *   the rows             GET intake/batches/{id}[?withFindings=true]&cursor=
 *                        - { batch, rows, nextCursor } - and, for a HOLDINGS
 *                        or TRANSACTIONS batch read without
 *                        core.registry.view, { rows: [], rowsWithheld:
 *                        { claim } } (withheldText)
 *   holding checks       a DAILY holdings batch once accepted (IP-108):
 *                        GET intake/batches/{id}/holding-checks?outcome=
 *                        &cursor= - { batchId, businessDate, outcomes,
 *                        counts, items, nextCursor } (holding-checks.js
 *                        holdingChecks), the server's 100 a page,
 *                        everything but MATCHED unless an outcome is chosen
 *                        (showsHoldingChecks, HOLDING_OUTCOMES,
 *                        readHoldingChecks)
 */

/** Summary keys the drawer shows in blocks of their own, not as facts. */
const OWN_BLOCKS = new Set(['findings', 'warnings', 'counts', 'total', 'truncated']);

/** camelCase as words: rowCount -> "Row count". A code - UTDOF, CSV_FORMULA - stays as it is. */
export function words(key) {
  if (/^[A-Z0-9_]+$/.test(String(key))) return String(key);
  const spaced = String(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const isPlain = (v) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

/** A value as the facts list shows it: a list of plain values joined (the first 10, and how many more), an object's plain fields as key: value. */
function shown(value) {
  if (isPlain(value)) return value === null ? '—' : String(value);
  if (Array.isArray(value)) {
    if (!value.every(isPlain)) return null;
    const first = value.slice(0, 10).join(', ');
    return value.length > 10 ? first + ' and ' + (value.length - 10) + ' more' : (first || '—');
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => isPlain(v));
    if (!entries.length || entries.length !== Object.keys(value).length) return null;
    return entries.map(([k, v]) => words(k) + ': ' + (v === null ? '—' : String(v))).join(' · ');
  }
  return null;
}

/**
 * The class summary as facts: [{ key, label, value }] for each summary field
 * that reads as text - whatever the class's parser put there (each class
 * summarises its own file) - leaving out the blocks the drawer shows itself.
 */
export function summaryFacts(summary) {
  if (!summary || typeof summary !== 'object') return [];
  return Object.entries(summary)
    .filter(([key]) => !OWN_BLOCKS.has(key))
    .map(([key, value]) => ({ key, label: words(key), value: shown(value) }))
    .filter((fact) => fact.value !== null);
}

/** The most rows a summary table shows: the rest are on the rows (a summary's lists are bounded at 50 server-side anyway). */
export const SUMMARY_TABLE_ROWS = 50;

/**
 * The class summary's lists of records as tables - a NAV file's perFund, a
 * class's per-fund counts - [{ key, label, columns: [{ key, label }], rows,
 * more }]: each list of flat objects (plain values only), its columns the
 * union of the records' keys in first-seen order, the first 50 rows. A
 * bounded list ({ items, total, truncated }, design 3) reads the same, with
 * how many more the server holds.
 */
export function summaryTables(summary) {
  if (!summary || typeof summary !== 'object') return [];
  const tables = [];
  for (const [key, value] of Object.entries(summary)) {
    if (OWN_BLOCKS.has(key)) continue;
    const bounded = value && !Array.isArray(value) && typeof value === 'object' && Array.isArray(value.items) ? value : null;
    const records = bounded ? bounded.items : value;
    if (!Array.isArray(records) || !records.length) continue;
    if (!records.every((r) => r && typeof r === 'object' && !Array.isArray(r) && Object.values(r).every(isPlain))) continue;
    const columns = [...new Set(records.flatMap((r) => Object.keys(r)))].map((k) => ({ key: k, label: words(k) }));
    const rows = records.slice(0, SUMMARY_TABLE_ROWS).map((r, i) => ({ key: i, ...r }));
    const total = bounded && Number.isInteger(bounded.total) ? bounded.total : records.length;
    tables.push({ key, label: words(key), columns, rows, more: Math.max(0, total - rows.length) });
  }
  return tables;
}

/**
 * A batch's warnings block, or null when it has none: each rule with its
 * count and first message, the listed warnings, and whether more are on the
 * rows than the summary lists.
 */
export function warningsOf(summary) {
  const w = summary?.warnings;
  if (!w || !(Number(w.total) > 0)) return null;
  const rules = Object.entries(w.byRule ?? {}).map(([rule, count]) => ({ rule, count, message: w.messages?.[rule] ?? null }))
    .sort((a, b) => b.count - a.count || a.rule.localeCompare(b.rule));
  const items = Array.isArray(w.items) ? w.items : [];
  return {
    total: Number(w.total),
    rules,
    items: items.map((item, i) => ({ key: i, ...item })),
    more: w.truncated ? Number(w.total) - items.length : 0,
  };
}

/** What the drawer says in place of rows a reader may not see (IP-103: client data). */
export function withheldText(rowsWithheld) {
  if (!rowsWithheld) return null;
  return 'This file holds client data: its rows are shown only to someone who also holds ' + (rowsWithheld.claim ?? 'core.registry.view') + '.';
}

/** One more page of rows under those shown, a row number listed once. */
export function appendRows(rows, page) {
  const seen = new Set(rows.map((r) => r.rowNo));
  return [...rows, ...(page ?? []).filter((r) => !seen.has(r.rowNo))];
}

/** The row page query: findings only or every row, from a cursor. */
export function rowsQuery({ withFindings = false, cursor = null } = {}) {
  return { withFindings: withFindings || undefined, cursor: cursor || undefined };
}

/** The holding-check views: the default (no outcome) is every check but MATCHED, as the server lists it. */
export const HOLDING_OUTCOMES = Object.freeze([
  { value: 'NOT_MATCHED', label: 'Not matched', outcome: undefined },
  { value: 'BREAK', label: 'Breaks', outcome: 'BREAK' },
  { value: 'UNKNOWN_CLIENT', label: 'Unknown clients', outcome: 'UNKNOWN_CLIENT' },
  { value: 'NOT_IN_FILE', label: 'Not in file', outcome: 'NOT_IN_FILE' },
  { value: 'MATCHED', label: 'Matched', outcome: 'MATCHED' },
]);

export const OUTCOME_COLOUR = Object.freeze({ MATCHED: 'green', BREAK: 'red', UNKNOWN_CLIENT: 'orange', NOT_IN_FILE: 'gold' });

/** A holding-check page's query for a view. */
export function holdingChecksQuery(view, cursor = null) {
  const chosen = HOLDING_OUTCOMES.find((o) => o.value === view) ?? HOLDING_OUTCOMES[0];
  return { outcome: chosen.outcome, cursor: cursor || undefined };
}

/**
 * One page of a batch's holding checks as the panel lists them. The server
 * answers { batchId, businessDate, outcomes, counts, items, nextCursor }: the
 * checks are its items (CON-1 - the panel once read a `checks` key nobody
 * sends, and showed every break as nothing to show).
 *
 * @param {object} api the Console API (api/endpoints.js)
 * @param {string} batchId
 * @param {string} view a HOLDING_OUTCOMES value
 * @param {string|null} [cursor] the previous page's nextCursor
 * @returns {Promise<{ checks: object[], nextCursor: string|null }>}
 */
export async function readHoldingChecks(api, batchId, view, cursor = null) {
  const page = await api.intake.holdingChecks(batchId, holdingChecksQuery(view, cursor));
  return { checks: Array.isArray(page?.items) ? page.items : [], nextCursor: page?.nextCursor ?? null };
}

/**
 * Whether the drawer shows a batch's holding checks: a DAILY holdings file
 * that was accepted (its checks are written by the accept), to a reader who
 * may see client data (else the server refuses the read).
 */
export function showsHoldingChecks(batch, { rowsWithheld = null } = {}) {
  return batch?.fileClass === 'HOLDINGS' && batch?.loadKind === 'DAILY'
    && (batch.state === 'ACCEPTED' || batch.state === 'PROCESSED') && !rowsWithheld;
}

/** A check's units side by side: the file's against the register's, with the ageing split when either has one. */
export function unitsPair(fileUnits, registerUnits) {
  return { file: fileUnits ?? null, register: registerUnits ?? null, differs: fileUnits != null && registerUnits != null && Number(fileUnits) !== Number(registerUnits) };
}
